import { FC, useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { useInView } from 'react-intersection-observer';
import { CallIntelConstant, CallIntelTypes } from '@frontend/api-call-intel';
import { AnimatedBadge } from '@frontend/components';
import { TSSRecoveredTagCalloutWrapper, useComponentEngagementShallowStore } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { FieldChangeEvent, ResolvedFieldProps, TextLink, useModalControl } from '@frontend/design-system';
import { useCallIntelContactTypesLabel, useCallIntelDefaultsShallowStore } from '../../hooks';
import { useOptionsProvider } from '../../hooks/use-options-provider';
import { CallFlagsData } from '../../types';
import { generateTrackingId, sortWithPriorityMap } from '../../utils';
import {
  DEFAULT_FIELD_EMPTY_VALUE,
  FormState,
  transformOptionsToBadgeOptions,
  transformOptionsToNestedBadgeOptions,
} from '../../utils/call-analysis-util';
import { FIELD_MAPPING } from '../../utils/constant';
import { Accept } from '../accept';
import { CallIntelChip, SchedulingChip } from '../chip';
import { EditableField } from '../editable-field';
import ChecklistBadgeDropdownMenu from '../menu/checklist-badge-dropdown-menu';
import NestedBadgeDropdownMenu from '../menu/nested-badge-dropdown-menu';
import RadioBadgeDropdownMenu from '../menu/radio-badge-dropdown-menu';
import SchedulingOutcomeDropdownMenu from '../menu/scheduling-outcome-dropdown-menu';
import { SuggestionModal } from '../modal/suggestion-modal';
import { Thumb } from '../thumb';

interface CommonFormFieldProps {
  isEditMode: boolean;
  callFlags: CallFlagsData;
  isDemoAccount: boolean;
  call?: CallIntelTypes.CallRecord;
  callFormState: FormState;
  unsavedEditChangesConfig: CallIntelTypes.UnsavedEditChangesConfig;
  setUnsavedEditChangesConfig: (config: CallIntelTypes.UnsavedEditChangesConfig) => void;
}

interface ContactTypeFormFieldProps extends CommonFormFieldProps, ResolvedFieldProps<'radio'> {}

export const ContactTypeFormField: FC<ContactTypeFormFieldProps> = ({
  call,
  callFlags,
  isEditMode,
  isDemoAccount,
  callFormState,
  ...fieldProps
}) => {
  const { t } = useTranslation('callIntelInsights');
  const { isFailedCall, isSkippedCall } = callFlags;
  const contactTypeLabelMap = useCallIntelContactTypesLabel();
  const { chipVariants } = useCallIntelDefaultsShallowStore('chipVariants');
  const { getOptions } = useOptionsProvider();

  return (
    <>
      <EditableField
        editMode={isEditMode}
        label={t('Contact Type')}
        displayComponent={
          !isFailedCall && !isSkippedCall ? (
            <CallIntelChip
              chipVariants={chipVariants}
              dataKey={callFormState.contactType ?? ''}
              label={
                callFormState.contactType
                  ? contactTypeLabelMap?.[callFormState.contactType] ?? t('Unknown')
                  : t('Unknown')
              }
            />
          ) : (
            FIELD_MAPPING[CallIntelTypes.AnalysisTypeEnum.ANALYSIS_TYPE_CONTACT_TYPE]?.emptyValue
          )
        }
        editComponent={
          <RadioBadgeDropdownMenu
            name='contactType'
            isLoading={false}
            showSaveChanges={false}
            trackingIds={{
              fieldId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'contact-type-click',
                isDemoAccount,
              }),
              optionId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'contact-type-${menuOption}',
                isDemoAccount,
              }),
              saveBtnId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'contact-type-save-btn-click',
                isDemoAccount,
              }),
            }}
            useFormLevelSave
            options={transformOptionsToBadgeOptions(getOptions('contactType'))}
            widthConfig={{ menuWidth: '180px' }}
            {...fieldProps}
          />
        }
        isEdited={call?.isFieldEdited?.isContactTypeEdited}
      />
    </>
  );
};

interface SchedulingOpportunityFieldProps extends CommonFormFieldProps {
  schedulingOpportunityFieldProps: ResolvedFieldProps<'radio'>;
  schedulingOutcomeFieldProps: ResolvedFieldProps<'radio'>;
}

export const SchedulingOpportunityField: FC<SchedulingOpportunityFieldProps> = ({
  call,
  callFlags,
  isEditMode,
  isDemoAccount,
  callFormState,
  schedulingOpportunityFieldProps,
  schedulingOutcomeFieldProps,
  unsavedEditChangesConfig,
  setUnsavedEditChangesConfig,
}) => {
  const { t } = useTranslation('callIntelInsights');
  const { nonPatient: nonPatientToken } = useTranslationTokens();
  const { isPoorAudio, isFailedCall, isSkippedCall } = callFlags;
  const { getOptions } = useOptionsProvider();

  const isOnlyPoorAudio = isPoorAudio && !isSkippedCall && !isFailedCall;
  const isSkippedOrFailedCall = isSkippedCall || isFailedCall;

  const handleSchedulingOpportunitySelection = (primaryValue: string, secondaryValue?: string) => {
    const schedulingOpportunity = primaryValue as CallIntelTypes.SchedulingOpportunityEnum;
    if (
      schedulingOpportunity === CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED &&
      !secondaryValue
    ) {
      schedulingOpportunityFieldProps.onChange({
        name: 'schedulingOpportunity',
        value: primaryValue,
      });
    } else if (
      schedulingOpportunity === CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_NOT_IDENTIFIED
    ) {
      schedulingOpportunityFieldProps.onChange({ name: 'schedulingOpportunity', value: primaryValue });
      schedulingOutcomeFieldProps.onChange({
        name: 'schedulingOutcome',
        value: 'none',
      });
    } else {
      schedulingOpportunityFieldProps.onChange({ name: 'schedulingOpportunity', value: primaryValue });
      schedulingOutcomeFieldProps.onChange({
        name: 'schedulingOutcome',
        value: secondaryValue,
      });
    }
  };

  const resetSchedulingOpportunitySelection = (primaryValue: string, secondaryValue?: string) => {
    const schedulingOpportunity = primaryValue as CallIntelTypes.SchedulingOpportunityEnum;
    if (
      schedulingOpportunity === CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED &&
      !secondaryValue
    ) {
      schedulingOpportunityFieldProps.onChange({
        name: 'schedulingOpportunity',
        value: CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_NOT_IDENTIFIED,
      });
    }
  };

  return (
    <>
      <EditableField
        editMode={isEditMode}
        label={t('Scheduling Opportunity')}
        displayComponent={
          !isFailedCall && !isSkippedCall && callFormState.schedulingOpportunity !== undefined ? (
            <Thumb
              up={
                callFormState.schedulingOpportunity ===
                CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED
              }
            />
          ) : (
            FIELD_MAPPING[CallIntelTypes.AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OPPORTUNITY]?.emptyValue
          )
        }
        editComponent={
          <NestedBadgeDropdownMenu
            name='schedulingOpportunity'
            isLoading={false}
            onChange={handleSchedulingOpportunitySelection}
            onDismiss={resetSchedulingOpportunitySelection}
            showSaveChanges={false}
            trackingIds={{
              fieldId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'scheduling-opportunity-click',
                isDemoAccount,
              }),
              optionId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'scheduling-opportunity-${menuOption}',
                isDemoAccount,
              }),
              saveBtnId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'scheduling-opportunity-save-btn-click',
                isDemoAccount,
              }),
            }}
            useFormLevelSave
            value={schedulingOpportunityFieldProps.value}
            subValue={schedulingOutcomeFieldProps.value}
            options={transformOptionsToNestedBadgeOptions(
              getOptions('schedulingOpportunity', 'record', { withoutLabel: true }),
              {
                [CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED]: getOptions(
                  'schedulingOutcome',
                  'record',
                  { withoutLabel: true }
                ),
              },
              {
                [CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED]: t('Opportunity'),
                [CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_NOT_IDENTIFIED]: t('No Opportunity'),
                [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED]: t('Scheduled'),
                [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED]: t('Unscheduled'),
              }
            )}
            widthConfig={{ menuWidth: '217px' }}
            unsavedEditChangesConfig={unsavedEditChangesConfig}
            setUnsavedEditChangesConfig={setUnsavedEditChangesConfig}
          />
        }
        isEdited={call?.isFieldEdited?.isSchedulingOpportunityEdited}
        showTooltip={
          !isSkippedOrFailedCall &&
          !isOnlyPoorAudio &&
          callFormState.contactType === CallIntelTypes.ContactTypeEnum.CONTACT_NOT_A_PATIENT &&
          callFormState.schedulingOpportunity === undefined
        }
        tooltipText={t("Scheduling Opportunity isn't analyzed for {{nonPatientToken}} calls.", {
          nonPatientToken,
        })}
      />
    </>
  );
};

interface SchedulingOutcomeFieldProps extends CommonFormFieldProps {
  schedulingOpportunityFieldProps: ResolvedFieldProps<'radio'>;
  schedulingOutcomeFieldProps: ResolvedFieldProps<'radio'>;
}

export const SchedulingOutcomeField: FC<SchedulingOutcomeFieldProps> = ({
  call,
  callFlags,
  isEditMode,
  isDemoAccount,
  callFormState,
  schedulingOpportunityFieldProps,
  schedulingOutcomeFieldProps,
  unsavedEditChangesConfig,
  setUnsavedEditChangesConfig,
}) => {
  const { t } = useTranslation('callIntelInsights');
  const { nonPatient: nonPatientToken } = useTranslationTokens();
  const { isPoorAudio, isFailedCall, isSkippedCall } = callFlags;
  const { getOptions } = useOptionsProvider();

  const isOnlyPoorAudio = isPoorAudio && !isSkippedCall && !isFailedCall;
  const isSkippedOrFailedCall = isSkippedCall || isFailedCall;

  const handleSchedulingOutcomeSelection = (event?: FieldChangeEvent) => {
    const value = event
      ? 'target' in event
        ? 'value' in event.target
          ? event?.target?.value
          : ''
        : event.value
      : '';

    if (value === 'none') {
      schedulingOutcomeFieldProps.onChange({
        name: 'schedulingOutcome',
        value,
      });
      schedulingOpportunityFieldProps.onChange({
        name: 'schedulingOpportunity',
        value: CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_NOT_IDENTIFIED,
      });
    } else {
      schedulingOutcomeFieldProps.onChange({
        name: 'schedulingOutcome',
        value,
      });
      schedulingOpportunityFieldProps.onChange({
        name: 'schedulingOpportunity',
        value: CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED,
      });
    }
  };

  return (
    <>
      <EditableField
        editMode={isEditMode}
        label={t('Scheduling Outcome')}
        displayComponent={
          isFailedCall || isSkippedCall || callFormState.schedulingOutcome === undefined ? (
            DEFAULT_FIELD_EMPTY_VALUE
          ) : callFormState.schedulingOpportunity ===
            CallIntelTypes.SchedulingOpportunityEnum.SCHEDULING_OPPORTUNITY_IDENTIFIED ? (
            <Accept
              accepted={
                callFormState.schedulingOutcome === CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED
              }
            />
          ) : (
            FIELD_MAPPING[CallIntelTypes.AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OUTCOME]?.emptyValue
          )
        }
        editComponent={
          <RadioBadgeDropdownMenu
            emptyValue={
              callFormState.schedulingOutcome !== undefined
                ? FIELD_MAPPING[CallIntelTypes.AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OUTCOME]?.emptyValue
                : DEFAULT_FIELD_EMPTY_VALUE
            }
            name='schedulingOutcome'
            isLoading={false}
            showSaveChanges={false}
            trackingIds={{
              fieldId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'scheduling-outcome-click',
                isDemoAccount,
              }),
              optionId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'scheduling-outcome-${menuOption}',
                isDemoAccount,
              }),
              saveBtnId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'scheduling-outcome-save-btn-click',
                isDemoAccount,
              }),
            }}
            useFormLevelSave
            value={schedulingOutcomeFieldProps.value}
            onChange={handleSchedulingOutcomeSelection}
            options={transformOptionsToBadgeOptions(
              getOptions('schedulingOutcome', 'record', { withoutLabel: true, includeEmptyOption: true }),
              {
                [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_SCHEDULED]: t('Scheduled'),
                [CallIntelTypes.SchedulingOutcomeEnum.SCHEDULING_OUTCOME_UNSCHEDULED]: t('Unscheduled'),
              }
            )}
            widthConfig={{ menuWidth: '217px' }}
            unsavedEditChangesConfig={unsavedEditChangesConfig}
            setUnsavedEditChangesConfig={setUnsavedEditChangesConfig}
          />
        }
        isEdited={call?.isFieldEdited?.isSchedulingOutcomeEdited}
        showTooltip={
          !isSkippedOrFailedCall &&
          !isOnlyPoorAudio &&
          callFormState.contactType === CallIntelTypes.ContactTypeEnum.CONTACT_NOT_A_PATIENT &&
          callFormState.schedulingOutcome === undefined
        }
        tooltipText={t("Scheduling Outcome isn't analyzed for {{nonPatientToken}} calls.", {
          nonPatientToken,
        })}
      />
    </>
  );
};

interface SchedulingOutcomeV2FieldProps extends CommonFormFieldProps {
  schedulingOutcomeV2FieldProps: ResolvedFieldProps<'radio'>;
  onTriggerAutoSave: (fieldName: keyof FormState) => void;
}

export const SchedulingOutcomeV2Field: FC<SchedulingOutcomeV2FieldProps> = ({
  call,
  callFlags,
  isEditMode,
  isDemoAccount,
  callFormState,
  schedulingOutcomeV2FieldProps,
  onTriggerAutoSave,
  ...rest
}) => {
  const { t } = useTranslation('callIntelInsights');
  const { isFailedCall, isSkippedCall, isPrivateCall } = callFlags;
  const { getOptions } = useOptionsProvider();
  const { ref, inView } = useInView({
    triggerOnce: true,
  });

  const componentId = 'call-intel-scheduling-outcome-v2-new-badge';
  const trackingSource = 'call-insight-panel-call-analysis';

  const { trackVisibility, getEngagementData } = useComponentEngagementShallowStore(
    'trackVisibility',
    'getEngagementData'
  );

  const { visibilityCount } = getEngagementData({ componentId, source: trackingSource });

  const currentOutcome = callFormState.schedulingOutcomeV2;

  const isRecoveredOpportunity =
    currentOutcome === CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL;

  const isUnscheduled = currentOutcome === CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED;

  const markRecoveredOpportunity = useCallback(() => {
    schedulingOutcomeV2FieldProps.onChange({
      name: 'schedulingOutcomeV2',
      value: CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL,
    });
    onTriggerAutoSave('schedulingOutcomeV2');
  }, [schedulingOutcomeV2FieldProps, onTriggerAutoSave]);

  const { displayComponent, subLabel, subDisplayComponent } = useMemo(() => {
    const isFailedOrSkipped = isFailedCall || isSkippedCall;

    const outcomesToHide = [
      CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_NOT_APPLICABLE,
      CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNKNOWN,
    ];

    if (!currentOutcome || isFailedOrSkipped || outcomesToHide.includes(currentOutcome)) {
      return {
        displayContent: FIELD_MAPPING[CallIntelTypes.AnalysisTypeEnum.ANALYSIS_TYPE_SCHEDULING_OUTCOME_V2]?.emptyValue,
        subLabel: undefined,
        subDisplayComponent: undefined,
      };
    }

    let computedDisplayComponent: React.ReactNode;
    let computedSubLabel: React.ReactNode = undefined;
    let computedSubDisplayComponent: React.ReactNode = undefined;

    if (isRecoveredOpportunity) {
      computedDisplayComponent = (
        <SchedulingChip
          call={call}
          disabled
          schedulingOutcome={CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED}
        />
      );

      computedSubLabel =
        isRecoveredOpportunity && !call?.isFieldEdited?.isSchedulingOutcomeEdited
          ? t('Updated by Call Intelligence')
          : t('Updated Manually');

      computedSubDisplayComponent = (
        <TSSRecoveredTagCalloutWrapper locationId={call?.locationId} isDemo={isDemoAccount} callInsights={call}>
          <SchedulingChip
            call={call}
            schedulingOutcome={CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL}
            trackingContext='call-insight-panel-call-analysis'
          />
        </TSSRecoveredTagCalloutWrapper>
      );
    } else if (isUnscheduled) {
      computedDisplayComponent = (
        <SchedulingChip
          call={call}
          schedulingOutcome={CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED}
        />
      );
      computedSubLabel = t('Update Status?');
      computedSubDisplayComponent = (
        <TextLink
          css={styles.iconBtn}
          onClick={markRecoveredOpportunity}
          size='small'
          weight='bold'
          trackingId={generateTrackingId({
            component: 'call-analysis-scheduling-outcome',
            context: 'recovered-opportunity-click',
            isDemoAccount,
          })}
        >
          <Icon name='check-small' size={12} />
          {t('Mark as Recovered Opportunity')}
        </TextLink>
      );
    } else {
      computedDisplayComponent = <SchedulingChip call={call} schedulingOutcome={currentOutcome} />;
    }

    return {
      subLabel: computedSubLabel,
      displayComponent: computedDisplayComponent,
      subDisplayComponent: computedSubDisplayComponent,
    };
  }, [
    isFailedCall,
    isSkippedCall,
    currentOutcome,
    isRecoveredOpportunity,
    isUnscheduled,
    call,
    t,
    isDemoAccount,
    markRecoveredOpportunity,
  ]);

  useEffect(() => {
    if (inView) {
      trackVisibility({
        componentId,
        source: trackingSource,
      });
    }
  }, [inView, trackVisibility]);

  return (
    <>
      <EditableField
        editMode={isEditMode}
        label={t('Scheduling Outcome')}
        subLabel={subLabel}
        displayComponent={displayComponent}
        subDisplayComponent={subDisplayComponent}
        editComponent={
          <SchedulingOutcomeDropdownMenu
            name='schedulingOutcomeV2'
            isLoading={false}
            showSaveChanges={false}
            trackingIds={{
              fieldId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'scheduling-outcome-v2-click',
                isDemoAccount,
              }),
              optionId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'scheduling-outcome-v2-${menuOption}',
                isDemoAccount,
              }),
              saveBtnId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'scheduling-outcome-v2-save-btn-click',
                isDemoAccount,
              }),
            }}
            useFormLevelSave
            options={transformOptionsToBadgeOptions(getOptions('schedulingOutcomeV2'))}
            widthConfig={{ menuWidth: '281px' }}
            {...schedulingOutcomeV2FieldProps}
            {...rest}
          />
        }
        isEdited={!isRecoveredOpportunity && call?.isFieldEdited?.isSchedulingOutcomeEdited}
        isPrivate={isPrivateCall}
        badge={
          (isRecoveredOpportunity || isUnscheduled) && visibilityCount < 3 ? (
            <div ref={ref}>
              <AnimatedBadge label={t('New')} />
            </div>
          ) : null
        }
      />
    </>
  );
};

interface CallSentimentFormFieldProps extends CommonFormFieldProps, ResolvedFieldProps<'radio'> {}

export const CallSentimentFormField: FC<CallSentimentFormFieldProps> = ({
  call,
  callFlags,
  isEditMode,
  isDemoAccount,
  callFormState,
  ...fieldProps
}) => {
  const { t } = useTranslation('callIntelInsights');
  const { nonPatient: nonPatientToken } = useTranslationTokens();
  const { isPoorAudio, isFailedCall, isSkippedCall, isPrivateCall } = callFlags;
  const { getOptions } = useOptionsProvider();
  const isOnlyPoorAudio = isPoorAudio && !isSkippedCall && !isFailedCall;
  const isSkippedOrFailedCall = isSkippedCall || isFailedCall;
  const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

  return (
    <>
      <EditableField
        editMode={isEditMode}
        label={t('Customer Sentiment')}
        displayComponent={
          !isFailedCall && !isSkippedCall && callFormState.callSentiment ? (
            <CallIntelChip
              as='chip'
              chipVariants={chipVariants}
              dataKey={callFormState.callSentiment}
              label={dataLabels.sentiments?.[callFormState.callSentiment] || callFormState.callSentiment}
              key={callFormState.callSentiment}
            />
          ) : (
            FIELD_MAPPING[CallIntelTypes.AnalysisTypeEnum.ANALYSIS_TYPE_SENTIMENT]?.emptyValue
          )
        }
        editComponent={
          <RadioBadgeDropdownMenu
            name='callSentiment'
            isLoading={false}
            showSaveChanges={false}
            trackingIds={{
              fieldId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'sentiment-click',
                isDemoAccount,
              }),
              optionId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'sentiment-${menuOption}',
                isDemoAccount,
              }),
              saveBtnId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'sentiment-save-btn-click',
                isDemoAccount,
              }),
            }}
            useFormLevelSave
            {...fieldProps}
            options={transformOptionsToBadgeOptions(getOptions('sentiment'))}
          />
        }
        isEdited={call?.isFieldEdited?.isSentimentEdited}
        isPrivate={isPrivateCall}
        showTooltip={
          !isSkippedOrFailedCall &&
          !isOnlyPoorAudio &&
          callFormState.contactType === CallIntelTypes.ContactTypeEnum.CONTACT_NOT_A_PATIENT &&
          !callFormState.callSentiment
        }
        tooltipText={t("Customer Sentiment isn't analyzed for {{nonPatientToken}} calls.", {
          nonPatientToken,
        })}
      />
    </>
  );
};

interface AppointmentTypeFormFieldProps extends CommonFormFieldProps, ResolvedFieldProps<'checklist'> {}

export const AppointmentTypeFormField: FC<AppointmentTypeFormFieldProps> = ({
  call,
  callFlags,
  isEditMode,
  isDemoAccount,
  callFormState,
  ...fieldProps
}) => {
  const { t } = useTranslation('callIntelInsights');
  const { nonPatient: nonPatientToken } = useTranslationTokens();
  const { isPoorAudio, isFailedCall, isSkippedCall, isPrivateCall } = callFlags;
  const { getOptions } = useOptionsProvider();
  const isOnlyPoorAudio = isPoorAudio && !isSkippedCall && !isFailedCall;
  const isSkippedOrFailedCall = isSkippedCall || isFailedCall;
  const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

  const {
    modalProps: suggestionModalProps,
    openModal: openSuggestionModal,
    closeModal: closeSuggestionModal,
  } = useModalControl();
  return (
    <>
      <EditableField
        label={t('Appointment Type')}
        editMode={isEditMode}
        displayComponent={
          <div css={styles.chipsWrapper}>
            {!isFailedCall && !isSkippedCall && callFormState.appointmentTypes?.length
              ? callFormState.appointmentTypes.map((appointmentType) => (
                  <CallIntelChip
                    as='category'
                    chipVariants={chipVariants}
                    dataKey={appointmentType}
                    label={dataLabels.appointmentTypes?.[appointmentType] || appointmentType}
                    key={appointmentType}
                  />
                ))
              : FIELD_MAPPING[CallIntelTypes.AnalysisTypeEnum.ANALYSIS_TYPE_APPOINTMENT_TYPE]?.emptyValue}
          </div>
        }
        editComponent={
          <ChecklistBadgeDropdownMenu
            name='appointmentTypes'
            isLoading={false}
            onSuggestion={openSuggestionModal}
            showSaveChanges={false}
            trackingIds={{
              fieldId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'appointment-types-click',
                isDemoAccount,
              }),
              optionId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'appointment-types-${menuOption}',
                isDemoAccount,
              }),
              saveBtnId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'appointment-types-save-btn-click',
                isDemoAccount,
              }),
              suggestionBtnId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'appointment-types-suggestion-btn-click',
                isDemoAccount,
              }),
            }}
            useFormLevelSave
            options={transformOptionsToBadgeOptions(
              getOptions('appointmentTypes', 'record', {
                extraAppointmentTypes: callFormState.appointmentTypes,
              })
            )}
            {...fieldProps}
          />
        }
        isEdited={call?.isFieldEdited?.isAppointmentTypeEdited}
        isPrivate={isPrivateCall}
        showTooltip={!callFormState.appointmentTypes?.length && !isSkippedOrFailedCall && !isOnlyPoorAudio}
        tooltipText={
          callFormState.contactType !== CallIntelTypes.ContactTypeEnum.CONTACT_NOT_A_PATIENT
            ? t('No Appointment Type discussed on the call.')
            : t("Appointment Type isn't analyzed for {{nonPatientToken}} calls.", {
                nonPatientToken,
              })
        }
      />
      {suggestionModalProps.show && (
        <SuggestionModal
          modalProps={suggestionModalProps}
          onClose={closeSuggestionModal}
          fieldType={'appointmentTypes'}
          call={call}
          isDemoAccount={isDemoAccount}
        />
      )}
    </>
  );
};

interface CategoryFormFieldProps extends CommonFormFieldProps, ResolvedFieldProps<'checklist'> {}
export const CategoryFormField: FC<CategoryFormFieldProps> = ({
  call,
  callFlags,
  isEditMode,
  isDemoAccount,
  callFormState,
  ...fieldProps
}) => {
  const { t } = useTranslation('callIntelInsights');
  const { isFailedCall, isSkippedCall, isPrivateCall } = callFlags;
  const { getOptions } = useOptionsProvider();
  const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

  const {
    modalProps: suggestionModalProps,
    openModal: openSuggestionModal,
    closeModal: closeSuggestionModal,
  } = useModalControl();
  return (
    <>
      <EditableField
        editMode={isEditMode}
        label={t('Category')}
        displayComponent={
          <div css={styles.chipsWrapper}>
            {!isFailedCall && !isSkippedCall && callFormState.categories?.length
              ? callFormState.categories.map((category) => (
                  <CallIntelChip
                    as='category'
                    chipVariants={chipVariants}
                    dataKey={category}
                    label={dataLabels.categories?.[category] || category}
                    key={category}
                  />
                ))
              : FIELD_MAPPING[CallIntelTypes.AnalysisTypeEnum.ANALYSIS_TYPE_CATEGORY]?.emptyValue}
          </div>
        }
        editComponent={
          <ChecklistBadgeDropdownMenu
            name='categories'
            isLoading={false}
            onSuggestion={openSuggestionModal}
            showSaveChanges={false}
            trackingIds={{
              fieldId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'categories-click',
                isDemoAccount,
              }),
              optionId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'categories-${menuOption}',
                isDemoAccount,
              }),
              saveBtnId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'categories-save-btn-click',
                isDemoAccount,
              }),
              suggestionBtnId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'categories-suggestion-btn-click',
                isDemoAccount,
              }),
            }}
            useFormLevelSave
            options={transformOptionsToBadgeOptions(getOptions('categories'))}
            required
            label={t('Category')}
            {...fieldProps}
          />
        }
        isEdited={call?.isFieldEdited?.isCategoryEdited}
        isPrivate={isPrivateCall}
      />
      {suggestionModalProps.show && (
        <SuggestionModal
          modalProps={suggestionModalProps}
          onClose={closeSuggestionModal}
          fieldType={'categories'}
          call={call}
          isDemoAccount={isDemoAccount}
        />
      )}
    </>
  );
};

interface ServiceIssueFormFieldProps extends CommonFormFieldProps, ResolvedFieldProps<'checklist'> {}
export const ServiceIssuesFormField: FC<ServiceIssueFormFieldProps> = ({
  call,
  callFlags,
  isEditMode,
  isDemoAccount,
  callFormState,
  ...fieldProps
}) => {
  const { t } = useTranslation('callIntelInsights');
  const { isFailedCall, isSkippedCall, isPrivateCall } = callFlags;
  const { getOptions } = useOptionsProvider();
  const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

  const {
    modalProps: suggestionModalProps,
    openModal: openSuggestionModal,
    closeModal: closeSuggestionModal,
  } = useModalControl();
  return (
    <>
      <EditableField
        editMode={isEditMode}
        label={t('Service Issues')}
        displayComponent={
          <div css={styles.chipsWrapper}>
            {!isFailedCall && !isSkippedCall && callFormState.serviceQualityFlags?.length
              ? sortWithPriorityMap<CallIntelTypes.ServiceQualityFlagEnum>(
                  callFormState.serviceQualityFlags,
                  CallIntelConstant.ServiceQualityFlagPriorityMap
                ).map((serviceFlag) => (
                  <CallIntelChip
                    as='serviceQualityFlag'
                    chipVariants={chipVariants}
                    dataKey={serviceFlag}
                    label={dataLabels.serviceQualityFlags?.[serviceFlag] || serviceFlag}
                    key={serviceFlag}
                  />
                ))
              : FIELD_MAPPING[CallIntelTypes.AnalysisTypeEnum.ANALYSIS_TYPE_SERVICE_QUALITY_V2]?.emptyValue}
          </div>
        }
        editComponent={
          <ChecklistBadgeDropdownMenu
            name='serviceQualityFlags'
            isLoading={false}
            onSuggestion={openSuggestionModal}
            showSaveChanges={false}
            trackingIds={{
              fieldId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'service-quality-click',
                isDemoAccount,
              }),
              optionId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'service-quality-${menuOption}',
                isDemoAccount,
              }),
              saveBtnId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'service-quality-save-btn-click',
                isDemoAccount,
              }),
              suggestionBtnId: generateTrackingId({
                component: 'call-analysis-badge-dropdown',
                context: 'service-quality-suggestion-btn-click',
                isDemoAccount,
              }),
            }}
            useFormLevelSave
            options={transformOptionsToBadgeOptions(getOptions('serviceQualityFlag'))}
            label={t('Service Issues')}
            {...fieldProps}
          />
        }
        isEdited={call?.isFieldEdited?.isServiceQualityFlagEdited}
        isPrivate={isPrivateCall}
        badge={<AnimatedBadge label={t('Beta')} animated={false} bgColor={theme.colors.indigo50} />}
      />
      {suggestionModalProps.show && (
        <SuggestionModal
          modalProps={suggestionModalProps}
          onClose={closeSuggestionModal}
          fieldType={'serviceQualityFlags'}
          call={call}
          isDemoAccount={isDemoAccount}
        />
      )}
    </>
  );
};

const styles = {
  chipsWrapper: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
    justify-content: flex-end;

    @media screen and (max-width: ${breakpoints.small.min}px) {
      justify-content: flex-start;
    }
  `,
  iconBtn: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `,
};
